import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { LoginBackButton } from '@euler/app/flows/auth/components/BackButton';
import { useAuthTheme } from '@euler/app/flows/auth/hooks';
import { usePresentationThemeContext } from '@euler/app/tv/theme';
import { kDefaultIntroVideo } from '@euler/assets';
import { FontFamily, Heading } from '@euler/components/typography';
import { oemConfig } from '@euler/config/oem';
import { kAgentInfo } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { Agent } from '@euler/model/entity';
import { useServiceFactory } from '@euler/services/factory';
import { useNavigation } from '@react-navigation/native';
import { ResizeMode, Video } from 'expo-av';
import { FC, ReactNode, memo, useEffect, useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

export const LoginSide = memo(
  ({ width, style }: { width: number; style?: StyleProp<ViewStyle> }) => {
    const theme = useAuthTheme();
    const { colorMode } = usePresentationThemeContext();
    const oemTheme = oemConfig.theme?.[colorMode];
    const { rpx } = useResponsiveContext();
    const [agent, setAgent] = useState<Agent | null>();

    const { defaultStorageService } = useServiceFactory();

    useEffect(() => {
      defaultStorageService
        .get<Agent>(kAgentInfo)
        .then(res => {
          setAgent(res ?? null);
        })
        .catch(err => {
          console.error(err);
          setAgent(null);
        });
    }, [defaultStorageService]);

    return (
      <View
        css={`
          align-items: center;
          justify-content: center;
          width: ${width}px;
          background-color: ${theme.sidePanelBgColor};
        `}
        style={style}
      >
        <View
          style={StyleSheet.absoluteFill}
          css={`
            background-color: ${oemTheme?.auth?.sidePanelVideoBgColor ??
            theme.sidePanelVideoBgColor};
          `}
        >
          <Video
            source={
              oemConfig.introVideo?.source?.dark ??
              oemConfig.introVideo?.source?.light ??
              kDefaultIntroVideo
            }
            shouldPlay
            isLooping
            resizeMode={ResizeMode.COVER}
            pointerEvents="none"
            isMuted
            css={`
              position: absolute;
              left: ${oemTheme?.auth?.videoMargin ?? 0}px;
              top: ${oemTheme?.auth?.videoMargin ?? 0}px;
              bottom: ${oemTheme?.auth?.videoMargin ?? 0}px;
              right: ${oemTheme?.auth?.videoMargin ?? 0}px;
            `}
          />
        </View>

        <Heading
          text={
            agent === undefined ? '' : agent?.shortName ?? oemConfig.shortName
          }
          numberOfLines={1}
          allowFontScaling
          adjustsFontSizeToFit
          css={`
            font-size: ${rpx(48)}px;
            color: ${theme.sideTitleTextColor};
            margin-left: ${rpx(15)}px;
            margin-right: ${rpx(15)}px;
            text-align: center;
          `}
        />
        <Text
          numberOfLines={1}
          allowFontScaling
          adjustsFontSizeToFit
          css={`
            color: ${theme.sideSubTitleTextColor};
            font-family: ${FontFamily.NotoSans.Light};
            font-size: ${rpx(18)}px;
            letter-spacing: ${rpx(0.5)}px;
            margin-left: ${rpx(15)}px;
            margin-right: ${rpx(15)}px;
            text-align: center;
          `}
        >
          {oemConfig.slogon}
        </Text>
      </View>
    );
  },
);

export const useLoginLayout = () => {
  const {
    windowDimensions: { width, height },
    isTablet,
    isMobileLayout,
  } = useSystemMetrics();
  const mode = usePresentationMode();
  const sideWidth = width * 0.6;
  const showSidebar = !isMobileLayout && width > height;
  const formWidth =
    mode === 'presentation'
      ? width * 0.4 * 0.9
      : !showSidebar && isTablet
      ? Math.min(550, width * 0.5)
      : Math.min(450, width * 0.65);

  return {
    isMobileLayout,
    sideWidth,
    formWidth,
    width,
    height,
    showSidebar,
    isTablet,
  };
};

export const LoginContent: FC<{ children?: ReactNode }> = memo(
  ({ children }) => {
    const { formWidth, showSidebar, isTablet } = useLoginLayout();
    return (
      <SafeAreaView
        css={`
          flex: 1;
          align-items: ${!showSidebar && !isTablet ? 'stretch' : 'center'};
        `}
      >
        <View
          css={`
            flex: 1;
            width: ${!showSidebar && !isTablet ? '100%' : `${formWidth}px`};
            justify-content: ${!showSidebar && !isTablet
              ? 'flex-start'
              : 'center'};
          `}
        >
          {children}
        </View>
      </SafeAreaView>
    );
  },
);

export const LoginScreenView: FC<{ children?: ReactNode }> = memo(props => {
  const navigation = useNavigation();
  const canGoBack = navigation.canGoBack();
  const { rpx } = useResponsiveContext();
  const theme = useAuthTheme();

  return (
    <>
      <KeyboardAvoidingView
        behavior="padding"
        keyboardVerticalOffset={10}
        css={`
          flex: 1;
          background-color: ${theme.mainPanelBgColor};
        `}
      >
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            paddingTop: canGoBack ? rpx(80) : 0,
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          css={`
            flex: 1;
          `}
        >
          <LoginContent>{props.children}</LoginContent>
        </ScrollView>
      </KeyboardAvoidingView>
      {canGoBack && <LoginBackButton />}
    </>
  );
});
